import { ReactElement, SVGProps } from 'react';

import {
  IconCalendarFilled,
  IconCalendarOutline,
  IconRefreshCcw,
  IconTimerOutline,
} from '@iwoca/icons';
import { useLingui } from '@lingui/react/macro';
import { useFormContext } from 'react-hook-form';

import { useLocale } from '../../../../hooks/useLocale';
import { LOAN_DURATION_OPTIONS_DE } from '../../../GetStarted/GetStartedDE/loanOptions';
import { TMobileSignupForm } from '../../form/form';
import { useStageFieldTracking } from '../../form/MobileSignupFieldTracking';
import { useMobileSignupNavContext } from '../../hooks/useMobileSignupNavContext';
import { OptionCards, TOption } from '../../OptionCards/OptionCards';
import { StepLayout } from '../../StepLayout/StepLayout';

type TDuration = (typeof LOAN_DURATION_OPTIONS_DE)[number]['value'];

type TIcon = (props: SVGProps<SVGSVGElement>) => ReactElement;

const DURATION_ICONS: Record<TDuration, TIcon> = {
  few_months: IconTimerOutline,
  one_to_two_years: IconCalendarOutline,
  three_plus_years: IconCalendarFilled,
  ongoing: IconRefreshCcw,
};

// DIT-709. Card picker. On a fresh visit selecting a card advances immediately;
// once answered (e.g. on back-nav) the CTA is shown so the user isn't forced to
// re-tap their existing choice to move on. Reads/writes the shared mobile signup
// form store (DIT-664).
export const DurationScreen = () => {
  const { t } = useLingui();
  const { i18n } = useLocale();
  const { watch, setValue } = useFormContext<TMobileSignupForm>();
  const { goNext } = useMobileSignupNavContext();
  const { fieldProps } = useStageFieldTracking();

  const loanDuration = watch('loanDuration');

  const options: TOption<TDuration>[] = LOAN_DURATION_OPTIONS_DE.map(
    (option) => {
      const Icon = DURATION_ICONS[option.value];
      return {
        value: option.value,
        icon: <Icon width={24} height={24} aria-hidden />,
        label: i18n._(option.label),
      };
    },
  );

  return (
    <StepLayout
      stageTitle={t`Credit request`}
      heading={t`How long do you need the loan for?`}
      hideCta={!loanDuration}
      onContinue={goNext}
      fieldProps={fieldProps}
    >
      <OptionCards
        name="loanDuration"
        options={options}
        value={loanDuration || undefined}
        onChange={(value) => {
          const isFirstChoice = !loanDuration;
          setValue('loanDuration', value, { shouldDirty: true });
          // Auto-advance only on the first pick; once answered the CTA drives
          // navigation, so re-selecting just updates the choice.
          if (isFirstChoice) goNext();
        }}
      />
    </StepLayout>
  );
};
